const roundCoord = value => Math.round((value + Number.EPSILON) * 1e4) / 1e4;

// Needle base radius as a fraction of the outer radius. Mirrors react-gauge-chart's
// `15 * (width / 500)` with width ≈ 2 * outerRadius → 0.06 * outerRadius.
const NEEDLE_BASE_RATIO = 0.06;

// d3-style point on the gauge: angle measured clockwise from 12 o'clock.
const point = (cx, cy, radius, rad) => ({
  x: roundCoord(cx + radius * Math.sin(rad)),
  y: roundCoord(cy - radius * Math.cos(rad))
});

/**
 * Needle rotation in degrees for a given fraction. 0 → points left, 0.5 → up, 1 → right.
 */
export const needleAngleDeg = percent => Math.min(1, Math.max(0, percent)) * 180;

/**
 * Start/end angle (radians) of each gauge segment across the upward half-circle
 * (-π/2 … +π/2), proportional to the normalized arc lengths.
 * @param {number[]} arcsLength
 * @returns {{ start: number, end: number }[]}
 */
export const gaugeSegmentAngles = arcsLength => {
  const total = arcsLength.reduce((sum, value) => sum + value, 0);
  const span = Math.PI;
  const startAngle = -Math.PI / 2;
  let cursor = 0;

  return arcsLength.map(length => {
    const fraction = total > 0 ? length / total : 0;
    const start = startAngle + cursor * span;
    cursor += fraction;
    return { start, end: startAngle + cursor * span };
  });
};

const describeAnnularSector = (cx, cy, outerRadius, innerRadius, start, end) => {
  const outerStart = point(cx, cy, outerRadius, start);
  const outerEnd = point(cx, cy, outerRadius, end);
  const innerEnd = point(cx, cy, innerRadius, end);
  const innerStart = point(cx, cy, innerRadius, start);
  const largeArc = end - start > Math.PI ? 1 : 0;

  return (
    `M ${outerStart.x} ${outerStart.y} ` +
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y} ` +
    `L ${innerEnd.x} ${innerEnd.y} ` +
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y} Z`
  );
};

/**
 * Build the fillable arc-band paths for the gauge, one per arc length.
 * @returns {{ path: string, color: string }[]}
 */
export const buildGaugeArcs = ({ arcsLength, colors, cx, cy, outerRadius, innerRadius }) =>
  gaugeSegmentAngles(arcsLength).map((segment, index) => ({
    path: describeAnnularSector(cx, cy, outerRadius, innerRadius, segment.start, segment.end),
    color: colors[index]
  }));

/**
 * Baseline needle (pointing left, percent 0). The component rotates it about `origin`
 * via a CSS transform. Includes the pivot base-circle geometry.
 * @returns {{ path: string, base: { cx: number, cy: number, r: number }, origin: { x: number, y: number } }}
 */
export const buildNeedle = ({ cx, cy, outerRadius, needleScale }) => {
  const needleRadius = roundCoord(outerRadius * NEEDLE_BASE_RATIO);
  const needleLength = outerRadius * needleScale;
  const centerY = roundCoord(cy - needleRadius / 2);
  const centerX = roundCoord(cx);

  const apex = { x: roundCoord(cx - needleLength), y: centerY };
  const baseTop = { x: centerX, y: roundCoord(centerY + needleRadius) };
  const baseBottom = { x: centerX, y: roundCoord(centerY - needleRadius) };

  return {
    path: `M ${baseTop.x} ${baseTop.y} L ${apex.x} ${apex.y} L ${baseBottom.x} ${baseBottom.y} Z`,
    base: { cx: centerX, cy: centerY, r: needleRadius },
    origin: { x: centerX, y: centerY }
  };
};
